import { LoadingOutlined, PictureOutlined } from "@ant-design/icons";
import { Button, Card, Image, Skeleton, Space, Spin, Typography } from "antd";
import Cookies from "js-cookie";
import moment from "moment-timezone";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineLike } from "react-icons/ai";
import { BsCheckAll, BsFillPersonFill } from "react-icons/bs";
import { FaCopy, FaReply, FaShareAlt } from "react-icons/fa";
import { IoIosEyeOff } from "react-icons/io";
import { LuAlarmClock } from "react-icons/lu";
import { MdDelete } from "react-icons/md";
import { RiUnpinFill } from "react-icons/ri";
import { RxDrawingPinFilled } from "react-icons/rx";
import apiFactory from "../../api";
import eventIcon from "../../assets/event-icon.png";
import noImage from "../../assets/no-image.avif";
import {
  conversationType,
  MESSAGE_STATUS,
  REACTION_TYPE_LABEL,
} from "../../config/Constant";
import { useChatContext } from "../../context/ChatContext";
import { useMenuContext } from "../../context/MenuContext";
import { useInfoUser } from "../../store/UserStore";
import { getFileIcon } from "../../utils/SendFile";
import {
  audioTypes,
  handleCopy,
  onChangeUnreadConversation,
  pinnedMessage,
  videoTypes,
} from "../../utils/Utils";
import { formatShortTime, formatTime } from "../../utils/formatTime";
import ShowContent from "../ShowContent/ShowContent";
// import { MessageAvatar } from "../avatar/MessageAvatar";
import PollBox from "../customComponent/PollBox";
import { useImageLoader } from "../customHook/ImageLoader";
import { PinnedModal } from "../modal/PinnedModal";
import { ReactionModal } from "../modal/ReactionModal";
import { MessageRemoveModal } from "../modal/chat/MessageRemoveModal";
import StickerItem from "../moodPack/sticker/StickerItem";
import GridPhotos from "./GridPhotos";
import MediaAudioContent from "./MediaAudioContent";
import { PreviewFile } from "./PreviewFile";
import { PreviewVideoContent } from "./PreviewVideoContent";
import { ScheduleMessage } from "./ScheduleMessage";
import { VideoFile } from "./VideoFile";
import { renderMessage } from "./renderMessage";
import { useAuthLayoutContext } from "../../context/AuthLayoutContext";
import { MemberAvatar } from "../avatar/ProfileAvatar";

const { Text } = Typography;

const PollOptionSkeleton = () => (
  <div className="poll-option-skeleton">
    <div className="poll-option-skeleton-bg" />
    <div className="poll-option-skeleton-content">
      <Skeleton.Input style={{ width: "100%", height: "40px" }} active />
      <Skeleton.Input style={{ width: "100%", height: "40px" }} active />
    </div>
  </div>
);

const SenderName = ({ message, isNotShowAvatar }) => {
  const { user } = useInfoUser();
  const { orgList } = useAuthLayoutContext();

  const sender = orgList?.find((org) => org?.userCode === message?.senderId);

  if (message?.senderId === user?.userId || isNotShowAvatar) return;

  if (!sender)
    return (
      <div className="text-[#2A55B9] font-medium">{message?.senderName}</div>
    );

  return <div className="text-[#2A55B9] font-medium">{sender?.name}</div>;
};

const NotificationMessage = ({ resultTs, icon, content, bgContent }) => {
  return (
    <>
      <div className="notification-ts">{formatTime(resultTs)}</div>
      <div className={bgContent + " notification-content"}>
        <div className="inline-block align-middle">{icon ? icon : null}</div>
        <div className="inline-block align-middle">{content}</div>
      </div>
    </>
  );
};

const NotificationSystem = ({
  resultTs,
  content,
  isLoadingOnPin,
  isMessagePinned,
  setIsLoadingOnPin,
  languageMap,
  message,
  setIsOpenModalPinnConfirm,
  isOpenModalPinnConfirm,
  pinnedMessages,
  viewTargetMessage,
}) => {
  return (
    <>
      <div className="notification-ts my-2">{formatTime(resultTs)}</div>
      <div className="flex justify-center">
        <div className="relative w-[70%] max-w-3xl group">
          <div
            className={`${
              viewTargetMessage?.messageId === message?.messageId
                ? "bg-[#d5f1de]"
                : "bg-[#E3EAFB]"
            } border-[#B8D7FF] border text-black px-4 py-3 rounded-2xl shadow-md w-full`}
          >
            <div className="flex items-start space-x-2 pr-12">
              <span className="text-lg mt-[2px]">📢</span>
              <div className="whitespace-pre-wrap break-words text-sm">
                {content}
              </div>
            </div>
          </div>

          {/* Action buttons  */}
          <div className="absolute right-[-56px] top-1/2 -translate-y-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <Button
              className="bg-[#2a56b9] text-white h-[24px] w-[24px] p-0 flex items-center justify-center"
              size="small"
              icon={<FaCopy size={15} />}
              onClick={() =>
                handleCopy(content?.replace(/@\[([^\]]+)\]\([^)]+\)/g, "@$1"))
              }
            />
            <Button
              className="bg-[#2a56b9] text-white h-[24px] w-[24px] p-0 flex items-center justify-center"
              size="small"
              title={
                isMessagePinned
                  ? languageMap?.["conversation.message.unpin"] ?? "Unpin"
                  : languageMap?.["conversation.message.pin"] ?? "Pin"
              }
              icon={
                isMessagePinned ? (
                  <RiUnpinFill size={15} />
                ) : (
                  <RxDrawingPinFilled size={15} />
                )
              }
              disabled={isLoadingOnPin}
              onClick={() => {
                if (!isMessagePinned && pinnedMessages?.length >= 3) {
                  setIsOpenModalPinnConfirm(true);
                  return;
                }
                pinnedMessage(message, setIsLoadingOnPin);
              }}
            />
          </div>
        </div>
      </div>

      {isOpenModalPinnConfirm && (
        <PinnedModal
          title={languageMap?.["pin.modal.title"] ?? "Update pin message list"}
          content={
            languageMap?.["pin.modal.content"] ??
            "Your pin message has been limit. Old pin message will be removed to update new pin message"
          }
          onCancel={() => setIsOpenModalPinnConfirm(false)}
          open={isOpenModalPinnConfirm}
          onConfirm={() => pinnedMessage(message, setIsLoadingOnPin)}
          pinnedMessages={pinnedMessages}
        />
      )}
    </>
  );
};

const MessageAction = ({
  selectedConversation,
  message,
  setQuotation,
  inputSendRef,
  setIsShareMessageModal,
  setDataShare,
  requestUuid,
  isMobile,
  selectedReaction,
  handleReactionClick,
  showAction,
  setShowAction,
  handleTouchEnd,
}) => {
  const { languageMap, user } = useInfoUser();
  const [removingMessageId, setRemovingMessageId] = useState(null);
  const [openModalMessageRemove, setOpenModalMessageRemove] = useState(false);
  const { removeMessage, pinnedMessages } = useChatContext();
  const [isLoadingOnPin, setIsLoadingOnPin] = useState(false);
  const [isOpenModalPinnConfirm, setIsOpenModalPinnConfirm] = useState(false);

  const isMessagePinned = pinnedMessages?.some(
    (pinned) => pinned?.messageId === message?.messageId,
  );

  const closeModalMessageRemove = () => {
    setOpenModalMessageRemove(false);
  };

  const handleOnMessageRemoveConfirm = async () => {
    if (isMessagePinned) {
      pinnedMessage(message, setIsLoadingOnPin);
    }

    removeMessage(removingMessageId);
    closeModalMessageRemove();
  };

  useEffect(() => {
    setIsLoadingOnPin(false);
  }, [pinnedMessages]);

  if (
    [MESSAGE_STATUS.REMOVE_MESSAGE, MESSAGE_STATUS.CHUNK]?.includes(
      message?.contentType,
    )
  )
    return;

  return (
    <>
      {isMobile && showAction && (
        <div
          className="fixed inset-0 z-[9999] bg-black bg-opacity-30"
          onClick={() => {
            setShowAction(false);
            handleTouchEnd();
          }}
        />
      )}

      {(isMobile ? showAction : true) && (
        <div
          className={`${
            isMobile
              ? "fixed bottom-0 left-0 right-0 z-[10000] bg-white rounded-t-xl px-4 pt-3 pb-6 shadow-[0_-2px_10px_rgba(0,0,0,0.1)]"
              : "message-action"
          }`}
        >
          {isMobile && (
            <div
              className="flex flex-col items-center gap-2"
              onClick={() => {
                setShowAction(false);
                handleTouchEnd();
              }}
            >
              {/* Reaction buttons */}
              <div className="flex justify-center flex-wrap gap-4">
                {REACTION_TYPE_LABEL.map((reaction) => (
                  <button
                    key={reaction?.type}
                    className={`text-2xl p-1 rounded-full transition-colors duration-200 ${
                      selectedReaction === reaction?.type
                        ? "bg-[#e0e0e0]"
                        : "bg-transparent"
                    }`}
                    onClick={() => handleReactionClick(reaction?.type)}
                  >
                    {renderMessage(reaction.label, 18)}
                  </button>
                ))}
              </div>

              {/* Action buttons */}
              {selectedConversation?.conversationStatus !== "INACTIVE" && (
                <div className="grid grid-flow-col auto-cols-max justify-center gap-2 w-full pt-2 border-t border-gray-200">
                  {message?.contentType !== "SYSTEM_MESSAGE" && (
                    <Button
                      className="text-[#2a56b9] shadow-sm py-1 rounded-md h-[40px] flex flex-col items-center justify-center gap-0.5"
                      size="small"
                      onClick={() => {
                        setQuotation({ ...message });
                        inputSendRef?.current?.focus();
                      }}
                    >
                      <FaReply size={16} />
                      <span className="text-[10px]">Trả lời</span>
                    </Button>
                  )}
                  <Button
                    className="text-[#2a56b9] shadow-sm py-1 rounded-md h-[40px] flex flex-col items-center justify-center gap-0.5"
                    size="small"
                    onClick={() => {
                      setDataShare(message);
                      setIsShareMessageModal(true);
                    }}
                  >
                    <FaShareAlt size={16} />
                    <span className="text-[10px]">Chia sẻ</span>
                  </Button>
                  {[
                    MESSAGE_STATUS.TEXT,
                    MESSAGE_STATUS.SYSTEM_MESSAGE,
                  ].includes(message.contentType) && (
                    <Button
                      className="text-[#2a56b9] shadow-sm py-1 rounded-md h-[40px] flex flex-col items-center justify-center gap-0.5"
                      size="small"
                      onClick={() =>
                        handleCopy(
                          message?.content?.replace(
                            /@\[([^\]]+)\]\([^)]+\)/g,
                            "@$1",
                          ),
                        )
                      }
                    >
                      <FaCopy size={16} />
                      <span className="text-[10px]">Sao chép</span>
                    </Button>
                  )}
                  {message?.senderId === user?.userId && (
                    <Button
                      className="text-[#2a56b9] shadow-sm py-1 rounded-md h-[40px] flex flex-col items-center justify-center gap-0.5"
                      size="small"
                      onClick={() => {
                        setRemovingMessageId(requestUuid);
                        setOpenModalMessageRemove(true);
                      }}
                    >
                      <MdDelete size={16} />
                      <span className="text-[10px]">Xóa</span>
                    </Button>
                  )}
                  <Button
                    className="text-[#2a56b9] shadow-sm py-1 rounded-md h-[40px] flex flex-col items-center justify-center gap-0.5"
                    size="small"
                    disabled={isLoadingOnPin}
                    title={
                      isMessagePinned
                        ? languageMap?.["conversation.message.unpin"] ?? "Unpin"
                        : languageMap?.["conversation.message.pin"] ?? "Pin"
                    }
                    onClick={() => pinnedMessage(message, setIsLoadingOnPin)}
                  >
                    {isMessagePinned ? (
                      <RiUnpinFill size={16} />
                    ) : (
                      <RxDrawingPinFilled size={16} />
                    )}
                    <span className="text-[10px]">
                      {isMessagePinned
                        ? languageMap?.["conversation.message.unpin"] ?? "Unpin"
                        : languageMap?.["conversation.message.pin"] ?? "Pin"}
                    </span>
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Desktop giữ nguyên logic */}
          {!isMobile &&
            selectedConversation?.conversationStatus !== "INACTIVE" && (
              <div className="flex gap-0.5 m-[0.5px]">
                {message?.contentType !== "SYSTEM_MESSAGE" && (
                  <Button
                    className="bg-[#2a56b9] text-white height-[18px] gap-[4px]"
                    size="small"
                    onClick={() => {
                      setQuotation({ ...message });
                      inputSendRef?.current?.focus();
                    }}
                    icon={<FaReply size={15} />}
                  />
                )}
                {message?.contentType !== "SYSTEM_MESSAGE" && (
                  <Button
                    className="bg-[#2a56b9] text-white height-[18px] gap-[4px]"
                    size="small"
                    icon={<FaShareAlt size={15} />}
                    onClick={() => {
                      setDataShare(message);
                      setIsShareMessageModal(true);
                    }}
                  />
                )}
                {[MESSAGE_STATUS.TEXT].includes(message.contentType) && (
                  <Button
                    className="bg-[#2a56b9] text-white height-[18px] gap-[4px]"
                    size="small"
                    icon={<FaCopy size={15} />}
                    onClick={() =>
                      handleCopy(
                        message?.content?.replace(
                          /@\[([^\]]+)\]\([^)]+\)/g,
                          "@$1",
                        ),
                      )
                    }
                  />
                )}
                {message?.senderId === user?.userId && (
                  <Button
                    className="bg-[#2a56b9] text-white height-[18px] gap-[4px]"
                    size="small"
                    icon={<MdDelete size={15} />}
                    onClick={() => {
                      setRemovingMessageId(requestUuid);
                      setOpenModalMessageRemove(true);
                    }}
                  />
                )}
                {!["SYSTEM_MESSAGE", "STICKER"].includes(
                  message?.contentType,
                ) && (
                  <Button
                    className="bg-[#2a56b9] text-white height-[18px] gap-[4px]"
                    size="small"
                    title={
                      isMessagePinned
                        ? languageMap?.["conversation.message.unpin"] ?? "Unpin"
                        : languageMap?.["conversation.message.pin"] ?? "Pin"
                    }
                    icon={
                      isMessagePinned ? (
                        <RiUnpinFill size={15} />
                      ) : (
                        <RxDrawingPinFilled size={15} />
                      )
                    }
                    disabled={isLoadingOnPin}
                    onClick={() => {
                      if (!isMessagePinned && pinnedMessages?.length >= 3) {
                        setIsOpenModalPinnConfirm(true);
                        return;
                      }
                      pinnedMessage(message, setIsLoadingOnPin);
                    }}
                  />
                )}
              </div>
            )}
        </div>
      )}

      {isOpenModalPinnConfirm && (
        <PinnedModal
          title={languageMap?.["pin.modal.title"] ?? "Update pin message list"}
          content={
            languageMap?.["pin.modal.content"] ??
            "Your pin message has been limit. Old pin message will be removed to update new pin message"
          }
          onCancel={() => setIsOpenModalPinnConfirm(false)}
          open={isOpenModalPinnConfirm}
          onConfirm={() => pinnedMessage(message, setIsLoadingOnPin)}
          pinnedMessages={pinnedMessages}
        ></PinnedModal>
      )}

      {openModalMessageRemove && (
        <MessageRemoveModal
          onCancel={closeModalMessageRemove}
          open={removingMessageId}
          onConfirm={handleOnMessageRemoveConfirm}
        />
      )}
    </>
  );
};

const MessageUnreadCount = ({ contentType, unreadCount, onClick }) => {
  if (MESSAGE_STATUS.REMOVE_MESSAGE === contentType || unreadCount <= 0)
    return <div></div>;

  return (
    <Button
      className="bg-[#2a56b9] text-[white] h-[20px] gap-[4px] mt-2 zoom-09"
      size={"small"}
      onClick={onClick}
    >
      <div className="flex items-center gap-[3px]">
        <IoIosEyeOff size={12} />
        <span className="text-[10px]">{unreadCount}</span>
      </div>
    </Button>
  );
};

const MessageEmotion = ({
  selectedConversation,
  message,
  totalReaction,
  uniqueReactionTypes,
  setOpenModalReaction,
  selectedReaction,
  handleReactionClick,
}) => {
  if (
    [MESSAGE_STATUS.REMOVE_MESSAGE, MESSAGE_STATUS.CHUNK]?.includes(
      message?.contentType,
    )
  )
    return;

  const getReactionLabel = REACTION_TYPE_LABEL?.find((item) =>
    selectedReaction?.includes(item?.type),
  );

  const filterReaction = () => {
    const filterReactionList = REACTION_TYPE_LABEL?.filter((item) =>
      uniqueReactionTypes?.includes(item?.type),
    );

    if (!filterReactionList?.length) return <AiOutlineLike />;

    return filterReactionList?.map((item) => {
      return <span key={item?.type}>{renderMessage(item?.label, 18)}</span>;
    });
  };

  return (
    <div className="reaction-wrap">
      {selectedConversation?.conversationStatus !== "INACTIVE" && (
        <div className={`${!totalReaction && "emotion"}`}>
          {message?.contentType !== "SYSTEM_MESSAGE" &&
            message?.contentType !== MESSAGE_STATUS.CHUNK && (
              <>
                <div className="reaction-wrap">
                  {totalReaction > 0 && (
                    <Button
                      size="small"
                      shape="round"
                      key="LIKE"
                      className="reaction-lable"
                      style={{
                        paddingInlineStart: "4px",
                        paddingInlineEnd: "4px",
                      }}
                      onClick={() => {
                        if (uniqueReactionTypes?.length) {
                          setOpenModalReaction(true);
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        <span className="mt-[1px]">{filterReaction()}</span>
                        <span className="mb-[1px]">{totalReaction}</span>
                      </div>
                    </Button>
                  )}
                </div>

                <div className="reaction-lable-like cursor-pointer">
                  {!selectedReaction ? (
                    <button
                      className="selected-btn aiOutlineLike"
                      onClick={() => handleReactionClick("LIKE")}
                    >
                      <AiOutlineLike />
                    </button>
                  ) : (
                    <Button
                      size="small"
                      shape="circle"
                      className="selected-btn"
                      onClick={() => handleReactionClick(selectedReaction)}
                    >
                      {renderMessage(getReactionLabel?.label, 17)}
                    </Button>
                  )}

                  <div className="reaction-list emotion-action">
                    {REACTION_TYPE_LABEL.map((reaction) => (
                      <Button
                        size="middle"
                        shape="circle"
                        className="reaction-item"
                        key={reaction?.type}
                        onClick={() => handleReactionClick(reaction?.type)}
                        style={{
                          background:
                            selectedReaction === reaction?.type
                              ? "#ddd"
                              : "transparent",
                        }}
                      >
                        {renderMessage(reaction.label, 18)}
                      </Button>
                    ))}
                  </div>
                </div>
              </>
            )}
        </div>
      )}
    </div>
  );
};

const MessageContent = ({
  requestUuid,
  contentType,
  content,
  isMention,
  file,
  photos,
  selectedConversation,
  languageMap,
  isSelf,
  percentProgress,
  totalChunk,
  totalVolume,
  previewLink,
  createdAt,
  senderId,
  frameCount,
}) => {
  const { cancelUpload } = useChatContext();

  const shortName = file?.originalName;
  const extensions = shortName
    ?.substring(shortName?.lastIndexOf(".") + 1)
    .toLowerCase();

  switch (contentType) {
    case MESSAGE_STATUS.IMAGE:
      if (photos) {
        const isUploading = photos.some((p) => p.isUploading);

        return (
          <>
            <div className="relative">
              <div
                className={isUploading ? "opacity-60 pointer-events-none" : ""}
              >
                <GridPhotos
                  images={photos}
                  countFrom={5}
                  hideOverlay={true}
                  isSelf={isSelf}
                  content={content}
                  isMention={isMention}
                  createdAt={createdAt}
                  senderId={senderId}
                />
              </div>

              {isUploading && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <Spin
                    indicator={
                      <LoadingOutlined
                        style={{ fontSize: 28, color: "#2a56b9" }}
                        spin
                      />
                    }
                  />
                </div>
              )}
            </div>

            {content && content.length > 0 && photos.length > 1 && (
              <div className="mt-1">
                <ShowContent
                  content={content}
                  isMention={isMention}
                  maxLength={300}
                  hasImage={true}
                />
              </div>
            )}
          </>
        );
      }
      break;
    case MESSAGE_STATUS.FILE:
    case MESSAGE_STATUS.CHUNK:
      if (videoTypes.includes(extensions)) {
        return !file?.mainPath ? (
          <VideoFile
            requestUuid={requestUuid}
            file={{ ...file, createdAt: createdAt, userId: senderId }}
            contentType={contentType}
            percentProgress={percentProgress}
            totalVolume={totalVolume}
            cancelUpload={cancelUpload}
          />
        ) : (
          <PreviewVideoContent
            content={content}
            file={{ ...file, createdAt: createdAt, userId: senderId }}
            selectedConversation={selectedConversation}
            isSelf={isSelf}
          />
        );
      } else if (audioTypes.includes(extensions)) {
        return <MediaAudioContent file={file} shortName={shortName} />;
      }

      return (
        <PreviewFile
          requestUuid={requestUuid}
          content={content}
          file={file}
          contentType={contentType}
          percentProgress={percentProgress}
          totalVolume={totalVolume}
          cancelUpload={cancelUpload}
        />
      );
    case MESSAGE_STATUS.REMOVE_MESSAGE:
      return (
        <i className="remove-text">
          {languageMap?.[`messageList.conversation.remove`] ??
            "This message has been removed"}
        </i>
      );
    case MESSAGE_STATUS.STICKER:
      return (
        <StickerItem
          resourceId={content}
          frameCount={frameCount}
          size={120}
          autoPlayOnMount={true}
        />
      );
    default:
      return (
        <ShowContent
          content={content}
          isMention={isMention}
          previewLink={previewLink}
        />
      );
  }
};

const GenerateMessageContent = React.memo(
  ({
    isNotShowAvatar,
    isNotShowTime,
    message,
    setIsModalMembersReadUnread,
    setSelectedMessage,
    jumpToMessage,
    viewTargetMessage,
    inputSendRef,
    setIsShareMessageModal,
    setDataShare,
  }) => {
    const {
      selectedConversation,
      conversationList,
      handleUpdateConversationList,
      setQuotation,
    } = useMenuContext();

    const { orgList } = useAuthLayoutContext();
    const { isMobile, pinnedMessages, getMember } = useChatContext();

    const messageId = message?.messageId;
    const conversationId = message?.conversationId;
    const requestUuid = message?.requestUuid;
    const contentType = message?.contentType;
    const createdAt = moment(message?.createdAt);
    const content = message?.content;
    const previewLink = message?.previewLink;
    const isMention = message?.isMention;
    const progress = message?.totalChunkUploaded;
    const totalChunk = message?.totalChunk;
    const totalVolume = message?.totalVolume;
    const photos = message?.photos ? message?.photos : [];
    const [isSelf, setIsSelf] = useState(message?.isSelf);
    const file = message?.file || message?.files?.[0];
    const senderId = message?.senderId;
    const unreadCount = message?.unreadCount;
    const frameCount = message?.frameCount;
    const width = message?.width;

    const resultTs = formatTime(createdAt);

    const isPending = message?.status !== null && message?.status === "PENDING";
    const isReceived =
      message?.status !== null && message?.status === "RECEIVED";

    const [token] = useState(Cookies.get("access_token"));
    const { languageMap, user } = useInfoUser();
    const [openModalReaction, setOpenModalReaction] = useState(false);

    const {
      imageUrl,
      loading: isImageLoading,
      error: imageError,
    } = useImageLoader(message?.quotation?.photos?.[0]?.resourceId);

    const [selectedReaction, setSelectedReaction] = useState(
      message?.respondent ? message?.respondent[user?.userId] : null,
    );

    const [activeKey, setActiveKey] = useState("ALL");
    const [activeReactionList, setActiveReactionList] = useState();

    const [totalReaction, setTotalReaction] = useState(
      Object.values(message?.countReactionType || {}).reduce(
        (sum, count) => sum + count,
        0,
      ),
    );

    const [countReactionType, setCountReactionType] = useState(
      message?.countReactionType || {},
    );

    const [uniqueReactionTypes, setUniqueReactionTypes] = useState(() => {
      return Object.entries(message?.countReactionType || {})
        .filter(([key, value]) => value > 0)
        .map(([key]) => key);
    });

    const [showAction, setShowAction] = useState(false);
    let touchTimeoutRef = useRef(null);

    //state for pinned
    const [isLoadingOnPin, setIsLoadingOnPin] = useState(false);
    const [isOpenModalPinnConfirm, setIsOpenModalPinnConfirm] = useState(false);

    const isMessagePinned = pinnedMessages?.some(
      (pinned) => pinned?.messageId === message?.messageId,
    );

    const getMemberName = (message) => {
      const member = orgList?.find(
        (org) => org?.userCode === message?.senderId,
      );

      if (member) return member?.name;

      return message?.senderName;
    };

    const renderMessageStatus = () => {
      if (isPending) {
        return (
          <LoadingOutlined className="text-[12px] ml-1 text-blue-800" spin />
        );
      } else if (isReceived)
        return <BsCheckAll className="text-[16px] ml-1 text-[#2a56b9]" />;
      else return <></>;
    };

    const renderImage = useMemo(() => {
      if (isImageLoading) {
        return (
          <div
            className=" border-r border-[#ccc] pr-[10px]"
            style={{
              objectFit: "cover",
              objectPosition: "center",
            }}
          >
            <Spin />
          </div>
        );
      }

      if (imageError) {
        return (
          <Image
            className=" border-r border-[#ccc] pr-[10px]"
            src={noImage}
            height={50}
            preview={false}
            style={{
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
        );
      }

      if (imageUrl) {
        return (
          <Image
            className=" border-r border-[#ccc] pr-[10px]"
            src={imageUrl}
            height={50}
            preview={false}
            style={{
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
        );
      }

      return <></>;
    }, [isImageLoading, imageError, imageUrl]);

    const handleTouchStart = () => {
      if (isMobile) {
        touchTimeoutRef.current = setTimeout(() => {
          setShowAction(true);
        }, 500);
      }
    };

    const handleTouchEnd = () => {
      clearTimeout(touchTimeoutRef.current);
    };

    const handleReactionClick = (reactionType) => {
      if (reactionType === null || requestUuid === null || token === null)
        return;

      const updatedCountReactionType = { ...countReactionType };
      const updatedRespondent = { ...message?.respondent };

      if (selectedReaction !== null) {
        if (selectedReaction !== reactionType) {
          // Update reaction
          updatedCountReactionType[reactionType]++;
          updatedCountReactionType[selectedReaction]--;
          updatedRespondent[user?.userId] = reactionType;
        } else {
          // Delete reaction
          updatedCountReactionType[reactionType]--;
          delete updatedRespondent[user?.userId];
        }
      } else {
        // New reaction
        updatedCountReactionType[reactionType]++;
        updatedRespondent[user?.userId] = reactionType;
      }

      setSelectedReaction(updatedRespondent[user?.userId] || null);
      setCountReactionType(updatedCountReactionType);

      const payload = {
        requestUuid,
        conversationId,
        token,
        reactionType,
      };

      apiFactory.messageApi.reactionMessage(payload);

      onChangeUnreadConversation(
        conversationList.find(
          (conversation) =>
            conversation.conversationId === selectedConversation.conversationId,
        ),
        conversationList,
        handleUpdateConversationList,
      );
    };

    const getSender = () => {
      const sender = getMember(message?.senderId);

      if (!sender?.name) {
        return {
          name: message?.senderName,
          online: false,
          userId: message?.senderId,
          isDisabled: true,
        };
      }

      return sender;
    };

    const getMessageContentStyle = () => {
      const { contentType, files, messageId, createdAt } = message || {};
      const isFileWithoutPreview =
        contentType === MESSAGE_STATUS.FILE && !files?.[0]?.previewVideoImage;
      const isChunk = contentType === MESSAGE_STATUS.CHUNK;

      const minWidth = isMobile ?? "!min-w-[280px]";
      const style = isFileWithoutPreview || isChunk ? minWidth : "";

      const isTargetMsg = viewTargetMessage?.messageId === messageId;

      const baseClass = isSelf ? "sent-content" : "received-content";
      const highlightClass = isTargetMsg ? "!bg-[#d5f1de]" : "";

      return [baseClass, highlightClass, style].filter(Boolean).join(" ");
    };

    const getCssMessageContent = () => {
      let css = isSelf ? "sent" : "received";

      if (contentType === MESSAGE_STATUS.IMAGE && photos?.length > 1) {
        css += " !max-w-[30%]";
      }

      return css;
    };

    const voteMessageDisplay = () => {
      const voteMessageData = message?.voteMessageDto;

      if (voteMessageData && message?.contentType === "VOTE_MESSAGE") {
        return (
          <PollBox
            voteMessageData={voteMessageData}
            conversationId={conversationId}
          />
        );
      }

      return (
        <Card style={{ width: 350, minHeight: 350, padding: "16px" }}>
          <div className="m-1">
            <div className="poll-header">
              <Skeleton.Input
                style={{ width: "100%", height: "24px" }}
                active
              />
            </div>

            <Text type="secondary" className="poll-voter-info">
              0 người bình chọn
            </Text>

            <Space
              direction="vertical"
              size={8}
              style={{ width: "100%", marginTop: "12px" }}
            >
              {[...Array(3)].map((_, index) => (
                <PollOptionSkeleton key={index} />
              ))}
            </Space>

            <Skeleton.Button
              active
              block
              style={{ marginTop: "16px", height: "32px" }}
            />
          </div>
        </Card>
      );
    };

    const generateTargetMessage = () => {
      return (
        <div
          className={isSelf ? "sent-quotation" : "received-quotation"}
          onClick={() => jumpToMessage(message?.quotation)}
        >
          {message?.quotation?.contentType === MESSAGE_STATUS.TEXT && (
            <>
              <h2>{getMemberName(message?.quotation)}</h2>
              <div className="quotation-message">
                {renderMessage(
                  message?.quotation?.content
                    ?.replace(/@\[([^\]]+)\]\([^)]+\)/g, "@$1")
                    ?.split("\n")[0],
                  18,
                )}
              </div>
            </>
          )}

          {message?.quotation?.contentType ===
            MESSAGE_STATUS.REMOVE_MESSAGE && (
            <>
              <h2>{getMemberName(message?.quotation)}</h2>
              <div className="quotation-message">
                <i>
                  {languageMap?.[`messageList.conversation.remove`] ??
                    "This message has been removed"}
                </i>
              </div>
            </>
          )}

          {message?.quotation?.contentType === MESSAGE_STATUS.FILE && (
            <div className="quotation-message max-w-[240px] max-h-[300px] flex flex-row my-1 items-center">
              <div>
                {message?.quotation?.file?.originalName
                  ? getFileIcon(message?.quotation?.file?.originalName)
                  : message?.quotation?.file?.originalName}
              </div>
              <div className="flex flex-col quotation-message-inner">
                <h2>{getMemberName(message?.quotation)}</h2>
                <p className="quotation-message-text">
                  {message?.quotation?.file?.originalName}
                </p>
              </div>
            </div>
          )}

          {message?.quotation?.contentType === MESSAGE_STATUS.IMAGE && (
            <div className="flex items-center">
              <Image
                className=" border-r border-[#ccc] pr-[10px]"
                src={
                  message?.quotation?.photos?.[0]?.thumbnailPreviewPath ||
                  message?.quotation?.photos?.[0]?.mainPath
                }
                height={50}
                preview={false}
                style={{
                  objectFit: "cover",
                  objectPosition: "center",
                }}
              />
              <div className="flex gap-[4px] flex-col px-[10px] min-w-[100px] justify-center flex-1">
                <h2>{getMemberName(message?.quotation)}</h2>
                <div className="flex gap-[4px] text-xs text-[#5a6981]">
                  <PictureOutlined />
                  <div>
                    {message?.quotation?.content?.replace(
                      /@\[([^\]]+)\]\([^)]+\)/g,
                      "@$1",
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {message?.quotation?.contentType === MESSAGE_STATUS.STICKER && (
            <div className="flex items-center">
              <StickerItem
                resourceId={message?.quotation?.content}
                frameCount={message?.quotation?.frameCount}
                size={50}
                duration={0}
              />
              <div>
                <h2>{getMemberName(message?.quotation)}</h2>
                <span>[Sticker]</span>
              </div>
            </div>
          )}
        </div>
      );
    };

    useEffect(() => {
      setIsLoadingOnPin(false);
    }, [pinnedMessages]);

    useEffect(() => {
      if (message?.senderId !== user?.userId) return;
      setIsSelf(message?.senderId === user?.userId);
    }, [user]);

    useEffect(() => {
      const validCountReactionType = Object.fromEntries(
        Object.entries(countReactionType || {}).filter(
          ([key, value]) => Number.isFinite(value) && value > 0,
        ),
      );
      const totalReaction = Object.values(validCountReactionType).reduce(
        (sum, count) => sum + count,
        0,
      );
      setTotalReaction(totalReaction || 0);

      const recentThreeReactionTypes = Object.keys(
        validCountReactionType,
      ).slice(0, 3);

      setUniqueReactionTypes(recentThreeReactionTypes);
    }, [countReactionType]);

    useEffect(() => {
      if (message) {
        const validCountReactionType = Object.fromEntries(
          Object.entries(message?.countReactionType || {}).filter(
            ([key, value]) => Number.isFinite(value) && value > 0,
          ),
        );

        const select =
          message?.respondent && message?.respondent[user?.userId]
            ? message?.respondent[user?.userId] !== "CANCEL"
              ? message?.respondent[user?.userId]
              : null
            : selectedReaction;

        setSelectedReaction(select);

        const totalReaction = Object.values(validCountReactionType).reduce(
          (sum, count) => sum + count,
          0,
        );

        setTotalReaction(totalReaction || 0);
        setCountReactionType(message?.countReactionType || {});

        const recentThreeReactionTypes = Object.keys(
          validCountReactionType,
        ).slice(0, 3);

        setUniqueReactionTypes(recentThreeReactionTypes);

        setActiveReactionList(Object.keys(validCountReactionType) || []);

        if (!Object.keys(validCountReactionType)?.includes(activeKey)) {
          setActiveKey("ALL");
        }
      }
    }, [message?.countReactionType, message?.respondent]);

    switch (contentType) {
      case MESSAGE_STATUS.RENAME_GROUP: {
        if (selectedConversation?.type === conversationType.SYSTEM) return null;

        const contentStr = content?.split("changed the group name to ");

        const notification =
          user?.language !== "kr" ? (
            <span
              className="inline text-[white]"
              style={{ wordBreak: "break-word" }}
            >
              <b>{contentStr?.[0]}</b>
              <span>
                {languageMap?.[
                  "messageList.conversation.changedTheGroupNameTo"
                ] ?? "changed the group name to"}
              </span>
              <span> "</span>
              <b>{contentStr?.[1]}</b>
              <span>"</span>
            </span>
          ) : (
            <span
              className="inline text-[white]"
              style={{ wordBreak: "break-word" }}
            >
              <b>{contentStr?.[0]}</b>
              <span> - "</span>
              <b>{contentStr?.[1]}</b>
              <span>" </span>
              <span>
                {languageMap?.[
                  "messageList.conversation.changedTheGroupNameTo"
                ] ?? "changed the group name to"}
              </span>
            </span>
          );

        return (
          <NotificationMessage
            resultTs={resultTs}
            icon={<img src={eventIcon} className="pr-1" alt="" />}
            content={notification}
            bgContent="bg-[#2a56b9]"
          />
        );
      }

      case MESSAGE_STATUS.VOTE_MESSAGE: {
        if (selectedConversation?.type === conversationType.SYSTEM) return null;

        return (
          <>
            <div className="notification-ts">{formatTime(resultTs)}</div>
            <div className="notification-content">
              <div className="inline-block align-middle">
                {voteMessageDisplay()}
              </div>
            </div>
          </>
        );
      }

      case MESSAGE_STATUS.EXIT_GROUP: {
        if (selectedConversation?.type === conversationType.SYSTEM) return null;

        return (
          <NotificationMessage
            resultTs={resultTs}
            icon={
              <BsFillPersonFill color="black" className="text-[20px] pr-1" />
            }
            content={
              <span
                className="inline text-[white]"
                style={{ wordBreak: "break-word" }}
              >
                <b>{content} </b>
                <span>
                  {languageMap?.["messageList.conversation.leftTheGroup"] ??
                    "has left the group"}
                </span>
              </span>
            }
            bgContent="bg-[#2a56b9]"
          />
        );
      }

      case MESSAGE_STATUS.NEW_MEMBER: {
        if (selectedConversation?.type === conversationType.SYSTEM) return null;

        return (
          <NotificationMessage
            resultTs={resultTs}
            icon={
              <BsFillPersonFill color="white" className="text-[20px] pr-1" />
            }
            content={
              <span className="inline text-white flex gap-[4px] break-words">
                <span className="font-bold">{content}</span>
                <span>
                  {languageMap?.["messageList.join.greeting"] ??
                    " just joined this conversation"}
                </span>
              </span>
            }
            bgContent="bg-[#2a56b9]"
          />
        );
      }

      case MESSAGE_STATUS.CREATE_GROUP:
      case MESSAGE_STATUS.CREATE_PERSONAL:
      case MESSAGE_STATUS.CREATE_SELF: {
        if (selectedConversation?.type === conversationType.SYSTEM) return null;

        return (
          <NotificationMessage
            resultTs={resultTs}
            icon={<></>}
            content={
              <span
                className="inline text-[white]"
                style={{ wordBreak: "break-word" }}
              >
                {languageMap?.["messageList.conversation.new"] ??
                  "Welcome to group"}
              </span>
            }
            bgContent="bg-[#2a56b9]"
          />
        );
      }

      case MESSAGE_STATUS.REMOVE_MEMBER: {
        if (selectedConversation?.type === conversationType.SYSTEM) return null;

        return (
          <NotificationMessage
            resultTs={resultTs}
            icon={
              <BsFillPersonFill color="white" className="text-[20px] pr-1" />
            }
            content={
              <span
                className="inline text-[white]"
                style={{ wordBreak: "break-word" }}
              >
                <span className="font-bold">{content} </span>
                <span>
                  {languageMap?.["messageList.conversation.kickOut"] ??
                    "was kicked out of group"}
                </span>
              </span>
            }
            bgContent="bg-[#2a56b9]"
          />
        );
      }

      case MESSAGE_STATUS.UPDATE_ROLE_ADMIN: {
        if (selectedConversation?.type === conversationType.SYSTEM) return null;

        return (
          <NotificationMessage
            resultTs={resultTs}
            icon={
              <BsFillPersonFill color="white" className="text-[20px] pr-1" />
            }
            content={
              <span
                className="inline text-[white]"
                style={{ wordBreak: "break-word" }}
              >
                <span className="font-bold">{content} </span>
                <span>
                  {languageMap?.["messageList.conversation.changeAdmin"] ??
                    "was promoted to Admin"}
                </span>
              </span>
            }
            bgContent="bg-[#2a56b9]"
          />
        );
      }

      case MESSAGE_STATUS.UPDATE_GROUP_AVATAR: {
        if (selectedConversation?.type === conversationType.SYSTEM) return null;

        return (
          <NotificationMessage
            resultTs={resultTs}
            icon={<img src={eventIcon} className="pr-1" alt="" />}
            content={
              <span
                className="inline text-[white]"
                style={{ wordBreak: "break-word" }}
              >
                <b>{content}</b>
                <span>
                  {languageMap?.[
                    "messageList.conversation.changedGroupAvatar"
                  ] ?? "changed group avatar"}
                </span>
              </span>
            }
            bgContent="bg-[#2a56b9]"
          />
        );
      }

      case MESSAGE_STATUS.DISABLE_GROUP:
      case MESSAGE_STATUS.ENABLE_GROUP: {
        if (selectedConversation?.type === conversationType.SYSTEM) return null;

        const defaultText =
          contentType === MESSAGE_STATUS.DISABLE_GROUP
            ? "Group has been disabled"
            : "Group has been enabled";

        return (
          <NotificationMessage
            resultTs={resultTs}
            icon={
              <BsFillPersonFill color="black" className="text-[20px] pr-1" />
            }
            content={
              <span
                className="inline text-[white]"
                style={{ wordBreak: "break-word" }}
              >
                <b>{languageMap?.[content] ?? defaultText}</b>
              </span>
            }
            bgContent="bg-[#2a56b9]"
          />
        );
      }

      case MESSAGE_STATUS.PINNED_MESSAGE: {
        if (selectedConversation?.type === conversationType.SYSTEM) return null;

        return (
          <NotificationMessage
            resultTs={resultTs}
            icon={
              <RxDrawingPinFilled color="white" className="text-[20px] pr-1" />
            }
            content={
              <span
                className="inline text-[white]"
                style={{ wordBreak: "break-word" }}
              >
                <b>{content}</b>
                <span>
                  {" "}
                  {languageMap?.["PINNED_MESSAGE"] ?? "PINNED_MESSAGE"}
                </span>
              </span>
            }
            bgContent="bg-[#2a56b9]"
          />
        );
      }

      case MESSAGE_STATUS.UNPINNED_MESSAGE: {
        if (selectedConversation?.type === conversationType.SYSTEM) return null;

        return (
          <NotificationMessage
            resultTs={resultTs}
            icon={
              <RxDrawingPinFilled color="white" className="text-[20px] pr-1" />
            }
            content={
              <span
                className="inline text-[white]"
                style={{ wordBreak: "break-word" }}
              >
                <b>{content}</b>
                <span>
                  {" "}
                  {languageMap?.["UNPINNED_MESSAGE"] ?? "UNPINNED_MESSAGE"}
                </span>
              </span>
            }
            bgContent="bg-[#2a56b9]"
          />
        );
      }

      case MESSAGE_STATUS.PROMOTED_MESSAGE: {
        if (selectedConversation?.type === conversationType.SYSTEM) return null;

        return (
          <NotificationMessage
            resultTs={resultTs}
            icon={
              <RxDrawingPinFilled color="white" className="text-[20px] pr-1" />
            }
            content={
              <span
                className="inline text-[white]"
                style={{ wordBreak: "break-word" }}
              >
                <b>{content}</b>
                <span>
                  {" "}
                  {languageMap?.["PROMOTED_MESSAGE"] ?? "PROMOTED_MESSAGE"}
                </span>
              </span>
            }
            bgContent="bg-[#2a56b9]"
          />
        );
      }

      case MESSAGE_STATUS.SYSTEM_MESSAGE:
        return (
          <NotificationSystem
            resultTs={resultTs}
            content={content}
            isLoadingOnPin={isLoadingOnPin}
            isMessagePinned={isMessagePinned}
            setIsLoadingOnPin={setIsLoadingOnPin}
            languageMap={languageMap}
            message={message}
            setIsOpenModalPinnConfirm={setIsOpenModalPinnConfirm}
            isOpenModalPinnConfirm={isOpenModalPinnConfirm}
            pinnedMessages={pinnedMessages}
            viewTargetMessage={viewTargetMessage}
          />
        );

      case MESSAGE_STATUS.SCHEDULE:
      case MESSAGE_STATUS.UPDATE_SCHEDULE:
        return <ScheduleMessage message={message} />;

      case MESSAGE_STATUS.DELETE_SCHEDULE: {
        if (selectedConversation?.type === conversationType.SYSTEM) return null;

        return (
          <NotificationMessage
            resultTs={resultTs}
            icon={<LuAlarmClock color="white" className="text-[20px] pr-1" />}
            content={
              <span className="inline text-[white] flex gap-[2px]">
                <b>{getMemberName(message?.quotation)}</b>
                <b className="schedule-content">{content}</b>
              </span>
            }
            bgContent="bg-[#2a56b9]"
          />
        );
      }

      default:
        return (
          <div
            className="flex"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchEnd}
          >
            {!isSelf && (
              <>
                {isNotShowAvatar ? (
                  <div className="pl-[40px]" />
                ) : (
                  <MemberAvatar person={getSender()} />
                )}
              </>
            )}

            <div className="chat-messages">
              <SenderName message={message} isNotShowAvatar={isNotShowAvatar} />
              <div className={isSelf ? "sent-box" : "received-box"}>
                {isSelf && (
                  <MessageAction
                    isMobile={isMobile}
                    message={message}
                    setQuotation={setQuotation}
                    inputSendRef={inputSendRef}
                    setDataShare={setDataShare}
                    setIsShareMessageModal={setIsShareMessageModal}
                    requestUuid={requestUuid}
                    selectedConversation={selectedConversation}
                    selectedReaction={selectedReaction}
                    handleReactionClick={handleReactionClick}
                    showAction={showAction}
                    setShowAction={setShowAction}
                    handleTouchEnd={handleTouchEnd}
                  />
                )}
                <div
                  className={`${getCssMessageContent()} ${isPending ? "opacity-60" : ""}`}
                >
                  <div
                    className={
                      contentType !== MESSAGE_STATUS.STICKER &&
                      getMessageContentStyle()
                    }
                  >
                    {message?.quotation &&
                      message?.contentType !== MESSAGE_STATUS.REMOVE_MESSAGE &&
                      generateTargetMessage()}
                    <MessageContent
                      messageId={messageId}
                      requestUuid={requestUuid}
                      contentType={contentType}
                      content={content}
                      isMention={isMention}
                      file={file}
                      photos={photos}
                      selectedConversation={selectedConversation}
                      languageMap={languageMap}
                      isSelf={isSelf}
                      progress={progress}
                      totalChunk={totalChunk}
                      totalVolume={totalVolume}
                      percentProgress={message?.percentProgress}
                      previewLink={previewLink}
                      createdAt={message?.createdAt}
                      senderId={senderId}
                      frameCount={frameCount}
                      width={width}
                    />
                  </div>
                  {isSelf ? (
                    <div className="flex justify-between gap-[3px]">
                      <MessageEmotion
                        message={message}
                        totalReaction={totalReaction}
                        uniqueReactionTypes={uniqueReactionTypes}
                        setOpenModalReaction={setOpenModalReaction}
                        selectedReaction={selectedReaction}
                        handleReactionClick={handleReactionClick}
                        selectedConversation={selectedConversation}
                      />

                      {!isNotShowTime && (
                        <div className="w-full flex items-center mb-2 justify-between gap-2">
                          <div className="chat-message-time">
                            {formatShortTime(
                              createdAt,
                              languageMap,
                              user?.language,
                            )}
                            {renderMessageStatus()}
                          </div>

                          {!isPending && (
                            <MessageUnreadCount
                              contentType={contentType}
                              unreadCount={unreadCount}
                              onClick={() => {
                                setIsModalMembersReadUnread(true);
                                setSelectedMessage(message);
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex justify-between gap-[3px]">
                      {!isNotShowTime && (
                        <div className="w-full flex items-center mb-2 justify-between gap-2">
                          <MessageUnreadCount
                            contentType={contentType}
                            unreadCount={unreadCount}
                            onClick={() => {
                              setIsModalMembersReadUnread(true);
                              setSelectedMessage(message);
                            }}
                          />
                          <div className="chat-message-time">
                            {formatShortTime(
                              createdAt,
                              languageMap,
                              user?.language,
                            )}
                          </div>
                        </div>
                      )}

                      <MessageEmotion
                        message={message}
                        totalReaction={totalReaction}
                        uniqueReactionTypes={uniqueReactionTypes}
                        setOpenModalReaction={setOpenModalReaction}
                        selectedReaction={selectedReaction}
                        handleReactionClick={handleReactionClick}
                        selectedConversation={selectedConversation}
                      />
                    </div>
                  )}
                </div>
                {!isSelf && (
                  <MessageAction
                    isMobile={isMobile}
                    message={message}
                    setQuotation={setQuotation}
                    inputSendRef={inputSendRef}
                    setDataShare={setDataShare}
                    setIsShareMessageModal={setIsShareMessageModal}
                    requestUuid={requestUuid}
                    selectedConversation={selectedConversation}
                    selectedReaction={selectedReaction}
                    handleReactionClick={handleReactionClick}
                    showAction={showAction}
                    setShowAction={setShowAction}
                    handleTouchEnd={handleTouchEnd}
                  />
                )}
              </div>
            </div>

            {openModalReaction && (
              <ReactionModal
                conversationId={message?.conversationId}
                requestUuid={message?.requestUuid}
                reactionTypes={activeReactionList}
                openModalReaction={openModalReaction}
                setOpenModalReaction={setOpenModalReaction}
                activeKey={activeKey}
                setActiveKey={setActiveKey}
              />
            )}
          </div>
        );
    }
  },
);

export { GenerateMessageContent };
